import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import './Pricing.css';

const stripePromise = loadStripe('pk_test_51TWV2fHQiGJsglR8GYkorgFP5luW422X6BDFgVbKfeIlmTLa9lJoTvPGBR6ATyxZSujOVi224U4F9d58zrqKK0xz00qZKztvow');

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    yearlyPrice: 0,
    period: 'forever',
    color: '#30363d',
    accent: '#8b949e',
    features: [
      '1 chart panel',
      'Paper trading — all 3 brokers',
      'Basic trade journal',
      'Demo market data',
      'Small cap scanner',
      'Community access',
    ],
    missing: [
      'Live market data',
      'Unlimited charts',
      'CSV import',
      'Advanced indicators',
    ],
    cta: 'Current Plan',
    current: true,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    yearlyPrice: 23,
    period: 'month',
    color: '#2563eb',
    accent: '#60a5fa',
    highlight: true,
    badge: 'Most Popular',
    features: [
      'Unlimited chart panels',
      'All chart types & indicators',
      'Full trade journal + CSV import',
      'TRUE live market data',
      'Advanced momentum scanner',
      'Live news feed with alerts',
      'Priority support',
      'Early access to new features',
    ],
    missing: [],
    cta: 'Start Pro',
    monthlyPlan: 'pro_monthly',
    yearlyPlan: 'pro_yearly',
  },
  {
    id: 'family',
    name: 'Family',
    price: 49,
    yearlyPrice: 39,
    period: 'month',
    color: '#7c3aed',
    accent: '#a78bfa',
    badge: 'Best Value',
    features: [
      'Everything in Pro',
      'Up to 3 users',
      'Shared watchlists',
      'Family P&L dashboard',
      'PDT tracking per user',
      'Individual chart layouts',
      'Priority support',
      'Early access to new features',
    ],
    missing: [],
    cta: 'Start Family',
    monthlyPlan: 'family_monthly',
    yearlyPlan: 'family_yearly',
  },
];

const FAQS = [
  { q:'Is the free plan really free forever?', a:'Yes — no credit card required. You can use the paper trader, basic journal, and scanner completely free with no time limit.' },
  { q:'What makes your live data better than free brokers?', a:'Free brokers like Robinhood and Webull give you 15-minute delayed data. Our Finnhub integration gives you true real-time quotes so you can practice with actual market conditions.' },
  { q:'Can I cancel anytime?', a:'Absolutely. Cancel with one click from your profile page. No cancellation fees, no questions asked. You keep access until the end of your billing period.' },
  { q:'What is the PDT rule and why does it matter?', a:'The Pattern Day Trader rule currently requires $25,000 to make more than 3 day trades per week. This rule is under review — giving new traders much more flexibility soon.' },
  { q:'How does the Family plan work?', a:'One subscription covers up to 3 users. Perfect for trading with a parent, sibling, or partner. Each person gets their own journal, watchlist, and paper trading account.' },
  { q:'Do you offer refunds?', a:'Yes — if you are not satisfied within the first 7 days we will issue a full refund, no questions asked.' },
];

export default function Pricing({ onBack, user }) {
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [error, setError] = useState('');

  async function handleCheckout(plan) {
    if(plan.current) return;
    if(!user) {
      setError('Please sign in first to upgrade your plan.');
      return;
    }
    setError('');
    setLoading(plan.id);

    try {
      const planId = billing === 'yearly' ? plan.yearlyPlan : plan.monthlyPlan;
      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: planId, email: user.email }),
      });

      const data = await response.json();

      if(data.error) {
        setError(data.error);
        setLoading(null);
        return;
      }

      // Redirect to Stripe checkout
      if(data.url) {
        window.location.href = data.url;
      } else {
        const stripe = await stripePromise;
        await stripe.redirectToCheckout({ sessionId: data.sessionId });
      }
    } catch(err) {
      setError('Something went wrong. Please try again.');
      setLoading(null);
    }
  }

  return (
    <div className="pr-page">
      {/* HEADER */}
      <div className="pr-header">
        <button className="pr-back" onClick={onBack}>← Back to Platform</button>
        <div className="pr-header-inner">
          <div className="pr-badge">Simple, Transparent Pricing</div>
          <h1 className="pr-title">Start free.<br/>Upgrade when you're ready.</h1>
          <p className="pr-sub">No hidden fees. No tricks. Cancel anytime.</p>
          <div className="pr-billing-toggle">
            <button className={`pr-bt-btn ${billing==='monthly'?'active':''}`} onClick={()=>setBilling('monthly')}>Monthly</button>
            <button className={`pr-bt-btn ${billing==='yearly'?'active':''}`} onClick={()=>setBilling('yearly')}>
              Yearly <span className="pr-save-badge">Save 20%</span>
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{background:'#7f1d1d44',border:'1px solid #ef444444',borderRadius:8,padding:'10px 16px',margin:'16px 24px',fontSize:12,color:'#fca5a5',textAlign:'center'}}>
          {error}
          {!user && <button onClick={onBack} style={{background:'none',border:'none',color:'#60a5fa',cursor:'pointer',marginLeft:8,fontSize:12}}>Sign in →</button>}
        </div>
      )}

      {/* PLANS */}
      <div className="pr-plans-wrap">
        <div className="pr-plans">
          {PLANS.map(plan => {
            const displayPrice = billing==='yearly' ? plan.yearlyPrice : plan.price;
            return (
              <div key={plan.id} className={`pr-plan ${plan.highlight?'highlight':''}`}>
                {plan.badge && <div className="pr-plan-badge" style={{background:plan.color}}>{plan.badge}</div>}
                <div className="pr-plan-header">
                  <div className="pr-plan-name" style={{color:plan.accent}}>{plan.name}</div>
                  <div className="pr-plan-price">
                    {plan.price === 0 ? (
                      <span className="pr-price-free">Free</span>
                    ) : (
                      <>
                        <span className="pr-price-dollar">$</span>
                        <span className="pr-price-num">{displayPrice}</span>
                        <span className="pr-price-period">/{billing==='yearly'?'mo, billed yearly':'month'}</span>
                      </>
                    )}
                  </div>
                  {billing==='yearly' && plan.price > 0 && (
                    <div className="pr-yearly-save">Save ${(plan.price - displayPrice)*12}/year</div>
                  )}
                </div>
                <div className="pr-plan-features">
                  {plan.features.map(f=>(
                    <div key={f} className="pr-feature">
                      <span className="pr-check" style={{color:'#22c55e'}}>✓</span>
                      <span>{f}</span>
                    </div>
                  ))}
                  {plan.missing.map(f=>(
                    <div key={f} className="pr-feature missing">
                      <span className="pr-check" style={{color:'#30363d'}}>✗</span>
                      <span style={{color:'#30363d'}}>{f}</span>
                    </div>
                  ))}
                </div>
                <button
                  className={`pr-cta ${plan.highlight?'primary':plan.current?'current':''}`}
                  style={plan.highlight?{background:plan.color}:{borderColor:plan.color,color:plan.accent}}
                  onClick={()=>handleCheckout(plan)}
                  disabled={plan.current||loading===plan.id}
                >
                  {loading===plan.id ? (
                    <span className="pr-spinner"></span>
                  ) : (
                    plan.price > 0 ? `${plan.cta} — $${displayPrice}/mo` : plan.cta
                  )}
                </button>
                {plan.id !== 'free' && <div className="pr-guarantee">7-day money-back guarantee · Cancel anytime</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* COMPARISON TABLE */}
      <div className="pr-compare-wrap">
        <div className="pr-compare-title">Full Feature Comparison</div>
        <table className="pr-compare">
          <thead>
            <tr>
              <th style={{textAlign:'left'}}>Feature</th>
              <th>Free</th>
              <th style={{color:'#60a5fa'}}>Pro</th>
              <th style={{color:'#a78bfa'}}>Family</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Chart Panels','1','Unlimited','Unlimited'],
              ['Live Market Data','❌','✅','✅'],
              ['Paper Trading','✅','✅','✅'],
              ['Trade Journal','Basic','Full','Full'],
              ['CSV Import','❌','✅','✅'],
              ['Momentum Scanner','Basic','Advanced','Advanced'],
              ['Live News Feed','❌','✅','✅'],
              ['Users','1','1','Up to 3'],
              ['Shared Watchlists','❌','❌','✅'],
              ['Family Dashboard','❌','❌','✅'],
              ['Priority Support','❌','✅','✅'],
            ].map(([feat,...vals])=>(
              <tr key={feat}>
                <td style={{textAlign:'left',color:'#c9d1d9'}}>{feat}</td>
                {vals.map((v,i)=>(
                  <td key={i} style={{color:v==='✅'?'#22c55e':v==='❌'?'#30363d':'#c9d1d9'}}>
                    {v}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PDT CALLOUT */}
      <div className="pr-pdt-callout">
        <div className="pr-pdt-inner">
          <div className="pr-pdt-icon">📋</div>
          <div>
            <div className="pr-pdt-title">The PDT Rule Is Changing — Be Ready</div>
            <div className="pr-pdt-desc">New traders are about to get much more freedom to day trade. Practice now with real live data and have a massive edge when it changes.</div>
          </div>
          <button className="pr-cta primary" style={{background:'#2563eb',flexShrink:0,width:'auto',padding:'10px 20px'}} onClick={onBack}>
            Start Practicing Free →
          </button>
        </div>
      </div>

      {/* FAQ */}
      <div className="pr-faq-wrap">
        <div className="pr-faq-title">Frequently Asked Questions</div>
        <div className="pr-faqs">
          {FAQS.map((faq,i)=>(
            <div key={i} className="pr-faq" onClick={()=>setOpenFaq(openFaq===i?null:i)}>
              <div className="pr-faq-q">
                <span>{faq.q}</span>
                <span className="pr-faq-arrow">{openFaq===i?'▲':'▼'}</span>
              </div>
              {openFaq===i && <div className="pr-faq-a">{faq.a}</div>}
            </div>
          ))}
        </div>
      </div>

      <div className="pr-footer-note">
        The Groove Trading is an educational paper trading platform. Not financial advice.
        All trading involves risk. Past performance does not guarantee future results.
        Payments processed securely by Stripe.
      </div>
    </div>
  );
}